/**
 * Hook per la gestione delle Web Push Notifications
 * Gestisce: permission request, subscribe, unsubscribe, in-app notifications
 */
import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

/**
 * Converte una stringa base64url in Uint8Array per PushManager
 */
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export function usePushNotifications(token) {
  const [isSupported, setIsSupported] = useState(false);
  const [permission, setPermission] = useState("default");
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const inAppListenerRef = useRef(null);

  // Controlla supporto e stato sottoscrizione
  useEffect(() => {
    const checkSupport = async () => {
      if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        setIsSupported(false);
        return;
      }
      setIsSupported(true);
      setPermission(Notification.permission);

      try {
        const reg = await navigator.serviceWorker.ready;
        const sub = await reg.pushManager.getSubscription();
        setIsSubscribed(!!sub);
      } catch (e) {
        console.warn("Push check error:", e);
      }
    };
    checkSupport();
  }, []);

  // Ascolta messaggi push in-app dal service worker
  useEffect(() => {
    const handleSWMessage = (event) => {
      if (event.data && event.data.type === "PUSH_NOTIFICATION") {
        const { title, body, url } = event.data;
        // Mostra toast in-app (solo se la pagina è visibile)
        if (document.visibilityState === "visible") {
          toast(title, {
            description: body,
            duration: 5000,
            action: url && url !== "/operatore"
              ? {
                  label: "Vedi",
                  onClick: () => window.location.href = url,
                }
              : undefined,
          });
        }
      }
    };

    navigator.serviceWorker?.addEventListener("message", handleSWMessage);
    inAppListenerRef.current = handleSWMessage;

    return () => {
      navigator.serviceWorker?.removeEventListener("message", handleSWMessage);
    };
  }, []);

  // Sottoscrivi push notifications
  const subscribe = useCallback(async () => {
    if (!isSupported || !token) return;
    if (!VAPID_PUBLIC_KEY) {
      console.error("REACT_APP_VAPID_PUBLIC_KEY non configurata");
      return;
    }

    setIsLoading(true);
    try {
      // Richiedi permesso
      const perm = await Notification.requestPermission();
      setPermission(perm);
      if (perm !== "granted") {
        toast.error("Permesso notifiche negato. Abilitale dalle impostazioni del browser.");
        return;
      }

      const reg = await navigator.serviceWorker.ready;

      // Crea sottoscrizione push
      const applicationServerKey = urlBase64ToUint8Array(VAPID_PUBLIC_KEY);
      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });

      // Invia al backend
      await axios.post(
        `${API}/api/push/subscribe`,
        {
          subscription: subscription.toJSON(),
          userAgent: navigator.userAgent,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setIsSubscribed(true);
      toast.success("Notifiche push attivate!");
    } catch (e) {
      console.error("Subscribe error:", e);
      if (e.name === "NotAllowedError") {
        toast.error("Permesso notifiche bloccato. Abilitale nelle impostazioni.");
      } else {
        toast.error("Errore nell'attivazione delle notifiche.");
      }
    } finally {
      setIsLoading(false);
    }
  }, [isSupported, token]);

  // Disottoscrivi
  const unsubscribe = useCallback(async () => {
    if (!token) return;
    setIsLoading(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await axios.delete(`${API}/api/push/subscribe`, {
          data: { endpoint: sub.endpoint },
          headers: { Authorization: `Bearer ${token}` },
        });
        await sub.unsubscribe();
        setIsSubscribed(false);
        toast.success("Notifiche push disattivate.");
      }
    } catch (e) {
      console.error("Unsubscribe error:", e);
      toast.error("Errore nella disattivazione delle notifiche.");
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  return {
    isSupported,
    permission,
    isSubscribed,
    isLoading,
    subscribe,
    unsubscribe,
  };
}
